import React from 'react';

import { Caption, FsIconButton, Text } from '~/components/core';
import { EyeIcon } from '~/components/icons';
import useSecretReveal from '~/hooks/useSecretReveal';

import styles from './oneIncInternalAdmin.module.scss';

interface DetailProps {
  label: string;
  text?: string | null;
  isSecret?: boolean;
  // Renders in place of `text`, so a caller can show something that is not a string -- a status
  // chip, or a value with a copy control beside it -- without a second component that would then
  // drift from this one's label styling.
  children?: React.ReactNode;
  // The consequence line under a value, for the case where the value alone does not say what it
  // costs the reader: an unconfigured webhook signing key means inbound acknowledgements are
  // rejected, which "Not configured" on its own does not convey.
  helperText?: string;
  // For a secret the page does not load: the eye fetches it the first time it is opened.
  onReveal?: () => Promise<string | null | undefined>;
}

const Detail: React.FC<DetailProps> = ({ label, text, isSecret = false, children, helperText, onReveal }) => {
  const { isMasked, revealedText, toggleHidden } = useSecretReveal(onReveal);

  return (
    <div className={styles.detail}>
      <Caption variant={Caption.VARIANTS.LABEL}>{label}</Caption>
      <div className={styles.secret}>
        {children ?? (
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
            {isSecret && isMasked ? '..........' : (revealedText ?? text ?? '')}
          </Text>
        )}
        {isSecret ? <FsIconButton icon={EyeIcon} onClick={toggleHidden} /> : null}
      </div>
      {helperText ? (
        <div className={styles.helperText}>
          <Caption variant={Caption.VARIANTS.LABEL}>{helperText}</Caption>
        </div>
      ) : null}
    </div>
  );
};

export default Detail;
